import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Select, Switch } from "antd";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import MyPagination from "../../../components/global/pagination/Pagination.jsx";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import useModulePermissions from "../../../hooks/useModulePermissions.jsx";
import {
  GetSymbolCrmAllSymbolsApi,
  GetSymbolCrmGroupSymbolsApi,
  GetSymbolCrmGroupsApi,
  SetSymbolCrmGroupSymbolsApi,
} from "../../../api/request/settings/Symbol.js";

export default function SymbolGroupSymbols() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const symbolPermissions = useModulePermissions("setting", "symbol");
  const canView = !!symbolPermissions.view;
  const canUpdate = !!symbolPermissions.update;

  const [groupName, setGroupName] = useState(location.state?.groupName || "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [symbolCatalog, setSymbolCatalog] = useState([]);
  const [memberNames, setMemberNames] = useState([]);
  const [checkedNames, setCheckedNames] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pathFilter, setPathFilter] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const loadData = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [groupsRes, membersRes, allRes] = await Promise.all([
        GetSymbolCrmGroupsApi(),
        GetSymbolCrmGroupSymbolsApi(groupId),
        GetSymbolCrmAllSymbolsApi(
          new URLSearchParams({
            PageNumber: "1",
            PageSize: "10000",
            ApplyPagination: "false",
          }).toString(),
        ),
      ]);

      if (groupsRes?.data?.success) {
        const group = (groupsRes.data.data || []).find(
          (g) => String(g.id) === String(groupId),
        );
        if (group?.name) setGroupName(group.name);
        if (!group) {
          toast.error("Group not found.");
          navigate("/symbol");
          return;
        }
      }

      if (!membersRes?.data?.success || !allRes?.data?.success) {
        toast.error("Failed to load symbols for this group.");
        return;
      }

      setMemberNames((membersRes.data.data || []).map((m) => m.symbolName));
      setCheckedNames([]);
      setSymbolCatalog(
        (allRes.data.data || []).map((item) => ({
          symbolName: item.symbolName,
          description: item.description,
          symbolPath: item.symbolPath,
          digits: item.digits,
          symbolPoint: item.symbolPoint,
          contract: item.contract,
          groupId: item.groupId,
          groupName: item.groupName,
        })),
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.Message ||
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to load symbols.",
      );
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const persistMembers = useCallback(
    async (nextMembers) => {
      if (!groupId || !canUpdate) return false;
      setSaving(true);
      try {
        const { data } = await SetSymbolCrmGroupSymbolsApi(groupId, {
          symbolNames: nextMembers,
        });
        if (data?.success) {
          toast.success(data?.message);
          setMemberNames(nextMembers);
          setCheckedNames([]);
          await loadData();
          return true;
        }
        toast.error(data?.message || "Failed to update group symbols.");
        return false;
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.Message ||
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            "Failed to update group symbols.",
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [canUpdate, groupId, loadData],
  );

  const memberSet = useMemo(() => new Set(memberNames), [memberNames]);

  const pathOptions = useMemo(() => {
    const paths = new Set(
      symbolCatalog.map((item) => item.symbolPath).filter(Boolean),
    );
    return Array.from(paths)
      .sort((a, b) => a.localeCompare(b))
      .map((path) => ({ value: path, label: path }));
  }, [symbolCatalog]);

  const filteredRows = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return symbolCatalog.filter((item) => {
      if (!item.symbolName) return false;
      const inCurrent = memberSet.has(item.symbolName);
      const assignedElsewhere = Boolean(item.groupName) && !inCurrent;
      if (freeOnly && (inCurrent || assignedElsewhere)) return false;
      if (pathFilter && item.symbolPath !== pathFilter) return false;
      if (!q) return true;
      return (
        item.symbolName.toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q) ||
        (item.symbolPath || "").toLowerCase().includes(q)
      );
    });
  }, [symbolCatalog, memberSet, freeOnly, pathFilter, debouncedSearch]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pathFilter, freeOnly]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredRows.length / pageSize) || 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredRows.length, pageSize, page]);

  const selectableOnPage = useMemo(
    () => pageRows.filter((row) => !memberSet.has(row.symbolName)).map((row) => row.symbolName),
    [pageRows, memberSet],
  );

  const moveCount = useMemo(
    () =>
      checkedNames.filter((name) => {
        const row = symbolCatalog.find((item) => item.symbolName === name);
        return row?.groupName && !memberSet.has(name);
      }).length,
    [checkedNames, symbolCatalog, memberSet],
  );

  const handleAddChecked = useCallback(async () => {
    if (!checkedNames.length) return;
    const next = Array.from(new Set([...memberNames, ...checkedNames]));
    await persistMembers(next);
  }, [checkedNames, memberNames, persistMembers]);

  const handleRemoveMember = useCallback(
    async (symbolName) => {
      await persistMembers(memberNames.filter((name) => name !== symbolName));
    },
    [memberNames, persistMembers],
  );

  const columns = useMemo(
    () => [
      {
        title: "Symbol",
        dataIndex: "symbolName",
        key: "symbolName",
        render: (text) => text || "—",
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (text) => text || "—",
      },
      {
        title: "Path",
        dataIndex: "symbolPath",
        key: "symbolPath",
        render: (text) => text || "—",
      },
      {
        title: "Digits",
        dataIndex: "digits",
        key: "digits",
        width: 90,
        render: (value) => value ?? 0,
      },
      {
        title: "Contract",
        dataIndex: "contract",
        key: "contract",
        width: 120,
        render: (value) =>
          value === 0 || value ? Number(value).toLocaleString() : "—",
      },
      {
        title: "Group",
        key: "group",
        width: 160,
        render: (_, record) => {
          const inCurrent = memberSet.has(record.symbolName);
          if (inCurrent) {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                Added
              </span>
            );
          }
          if (record.groupName) {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                In {record.groupName}
              </span>
            );
          }
          return null;
        },
      },
      {
        title: "",
        key: "actions",
        width: 56,
        align: "center",
        render: (_, record) =>
          memberSet.has(record.symbolName) ? (
            <button
              type="button"
              className="p-1 border-0 bg-transparent cursor-pointer"
              aria-label={`Remove ${record.symbolName}`}
              title="Remove"
              disabled={!canUpdate || saving}
              onClick={() => handleRemoveMember(record.symbolName)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="#D91819"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                />
              </svg>
            </button>
          ) : null,
      },
    ],
    [canUpdate, handleRemoveMember, memberSet, saving],
  );

  if (!canView) {
    return (
      <div className="border border-gray-200 rounded-xl bg-white p-6 text-gray-600">
        You do not have permission to view symbols.
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <button
            type="button"
            className="text-sm text-primary font-medium mb-1"
            onClick={() => navigate("/symbol")}
          >
            ← Back to Symbol
          </button>
          <h1 className="text-xl font-semibold text-gray-900 m-0">Add symbols</h1>
          {groupName ? (
            <p className="text-sm text-gray-500 mt-1 mb-0">{groupName}</p>
          ) : null}
        </div>
        {canUpdate && (
          <LoadableButton
            type="button"
            className="bg-primary text-sm text-white font-semibold rounded-lg px-8 py-1.5 uppercase themeHover duration-500"
            lable="Add symbols"
            isLoading={saving}
            disabled={saving || loading || checkedNames.length === 0}
            loadingLable="Saving..."
            onClick={handleAddChecked}
          />
        )}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <Input.Search
          allowClear
          placeholder="Search symbol or description"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full lg:max-w-sm"
        />
        <Select
          allowClear
          showSearch
          placeholder="Any path"
          className="w-full lg:w-72"
          popupMatchSelectWidth={false}
          popupClassName="min-w-[28rem]"
          value={pathFilter || undefined}
          onChange={(value) => setPathFilter(value || "")}
          options={pathOptions}
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label || "")
              .toLowerCase()
              .includes(input.trim().toLowerCase())
          }
        />
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
          <Switch size="small" checked={freeOnly} onChange={setFreeOnly} />
          Free only
        </label>
        {moveCount > 0 && (
          <p className="text-sm text-gray-500 m-0">
            {moveCount} selected {moveCount === 1 ? "symbol" : "symbols"} will
            move from another group.
          </p>
        )}
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <TableWithSkeleton
          rowKey="symbolName"
          columns={columns}
          data={pageRows}
          loading={loading}
          page={page}
          pageSize={pageSize}
          className="symbol-group-symbols-table"
          rowSelection={
            canUpdate
              ? {
                  selectedRowKeys: checkedNames,
                  onChange: (keys) => {
                    const pageSet = new Set(selectableOnPage);
                    setCheckedNames((prev) => {
                      const kept = prev.filter((name) => !pageSet.has(name));
                      return [...kept, ...keys.filter((name) => pageSet.has(name))];
                    });
                  },
                  getCheckboxProps: (record) => ({
                    disabled: memberSet.has(record.symbolName) || saving,
                  }),
                }
              : undefined
          }
        />
      </div>

      {filteredRows.length > 0 && (
        <div className="mt-4">
          <MyPagination
            total={filteredRows.length}
            pageSize={pageSize}
            setPageSize={setPageSize}
            page={page}
            setPage={setPage}
          />
        </div>
      )}
    </div>
  );
}
